import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight, Menu, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Logo } from "@/components/brand/logo";
import { LogoIcon } from "@/components/brand/logo-icon";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { useSession } from "@/hooks/auth-hooks";
import { CONTACT_EMAIL } from "@/lib/constants";
import { buildBreadcrumbJsonLd, buildCoreJsonLd, JsonLd } from "@/lib/json-ld";

export const MAIN_CONTENT_ID = "main-content";

type PublicRouteTo =
	| "/blog"
	| "/dashboard"
	| "/help"
	| "/help/mcp"
	| "/help/mcp-tools"
	| "/help/quick-start"
	| "/help/tool-call-patterns"
	| "/login"
	| "/models"
	| "/pricing"
	| "/privacy"
	| "/security"
	| "/signup"
	| "/terms";

export const PUBLIC_NAV_ITEMS = [
	{ label: "Pricing", to: "/pricing" },
	{ label: "Docs", to: "/help" },
	{ label: "Blog", to: "/blog" },
] as const satisfies ReadonlyArray<{ label: string; to: PublicRouteTo }>;

export function PublicNavLinks() {
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	return (
		<>
			{PUBLIC_NAV_ITEMS.map((item) => {
				const isActive = pathname.startsWith(item.to);
				return (
					<Link
						key={item.label}
						to={item.to}
						aria-current={isActive ? "page" : undefined}
						className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
					>
						{item.label}
					</Link>
				);
			})}
		</>
	);
}

export function PublicHeader({ framed = false }: { framed?: boolean } = {}) {
	const { user, isPending } = useSession();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const [scrolled, setScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const breadcrumbJsonLd = buildBreadcrumbJsonLd(pathname);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 10);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	return (
		<>
			<JsonLd data={buildCoreJsonLd()} />
			{breadcrumbJsonLd ? <JsonLd data={breadcrumbJsonLd} /> : null}
			<div
				aria-hidden="true"
				className="pointer-events-none fixed inset-y-0 left-0 right-0 z-40 mx-auto max-w-6xl px-4 sm:px-6"
			>
				<div className="h-full border-x border-border" />
			</div>
			<header
				className={
					framed ? "sticky top-0 z-50" : "fixed top-0 left-0 right-0 z-50"
				}
			>
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div
						className={
							framed
								? "public-header-stack flex flex-col divide-y divide-border"
								: "bg-border p-px"
						}
					>
						<div
							className={`flex items-center justify-between rounded-md px-4 py-3 sm:px-6 sm:py-4 md:grid md:grid-cols-[1fr_auto_1fr] ${
								scrolled ? "bg-background/80 backdrop-blur-md" : "bg-background"
							}`}
						>
							<div className="flex items-center">
								<Logo size="sm" disableLink={false} />
							</div>
							<nav className="hidden items-center gap-1 md:flex">
								<PublicNavLinks />
							</nav>
							<div className="flex min-h-8 min-w-[10rem] items-center justify-end gap-2 sm:min-w-[13rem] md:min-w-0">
								{isPending ? (
									<>
										<div
											aria-hidden="true"
											className="hidden h-8 w-[3.5rem] rounded-md sm:block"
										/>
										<div
											aria-hidden="true"
											className="h-8 w-[5.375rem] rounded-md"
										/>
									</>
								) : user ? (
									<Link
										to="/dashboard"
										className="inline-flex h-8 items-center rounded-md bg-brand-accent px-3 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
									>
										Dashboard
									</Link>
								) : (
									<>
										<Link
											to="/login"
											className="hidden h-8 items-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent sm:inline-flex"
										>
											Log in
										</Link>
										<Link
											to="/onboard"
											className="inline-flex h-8 items-center rounded-md bg-brand-accent px-3 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
										>
											Top up first
										</Link>
									</>
								)}
								<AnimatedThemeToggler
									className="hidden md:inline-flex"
									size="sm"
								/>
								<button
									type="button"
									onClick={() => setMobileMenuOpen((open) => !open)}
									aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
									aria-expanded={mobileMenuOpen}
									className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm transition-colors hover:bg-accent md:hidden"
								>
									{mobileMenuOpen ? (
										<X className="size-4" />
									) : (
										<Menu className="size-4" />
									)}
								</button>
							</div>
						</div>
						{mobileMenuOpen && (
							<nav className="mt-px grid gap-px bg-border md:hidden">
								{PUBLIC_NAV_ITEMS.map((item) => (
									<Link
										key={item.label}
										to={item.to}
										onClick={() => setMobileMenuOpen(false)}
										className="rounded-md bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
									>
										{item.label}
									</Link>
								))}
							</nav>
						)}
					</div>
				</div>
			</header>
		</>
	);
}

export function PublicFooter({
	contentClassName = "",
	showCta = true,
}: {
	contentClassName?: string;
	showCta?: boolean;
} = {}) {
	return (
		<footer>
			<div
				className={`mx-auto max-w-6xl px-4 sm:px-6 ${contentClassName}`.trim()}
			>
				<div className="flex flex-col gap-px bg-border p-px">
					{showCta && <FooterCta />}
					<div className="grid grid-cols-2 gap-8 rounded-md bg-background px-6 py-12 sm:grid-cols-3 sm:px-8 lg:grid-cols-5">
						<div className="col-span-2 space-y-4 sm:col-span-3 lg:col-span-1">
							<Logo size="sm" disableLink />
							<p className="max-w-[24ch] text-sm text-muted-foreground">
								Create content assets from your AI assistant.
							</p>
						</div>

						<FooterColumn
							title="Product"
							links={[
								{ label: "Models", to: "/models" },
								{ label: "Pricing", to: "/pricing" },
								{ label: "Docs", to: "/help" },
								{ label: "Security", to: "/security" },
							]}
						/>
						<FooterColumn
							title="MCP"
							links={[
								{ label: "Quickstart", to: "/help/quick-start" },
								{ label: "MCP overview", to: "/help/mcp" },
								{ label: "Tools reference", to: "/help/mcp-tools" },
								{ label: "Patterns", to: "/help/tool-call-patterns" },
							]}
						/>
						<FooterColumn
							title="Account"
							links={[
								{ label: "Sign up", to: "/signup" },
								{ label: "Log in", to: "/login" },
								{ label: "Dashboard", to: "/dashboard" },
								{ label: "Support", href: `mailto:${CONTACT_EMAIL}` },
							]}
						/>
						<FooterColumn
							title="Legal"
							links={[
								{ label: "Terms", to: "/terms" },
								{ label: "Privacy", to: "/privacy" },
							]}
						/>
					</div>
					<div className="flex flex-col gap-4 rounded-md bg-background px-6 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-8">
						<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
							<span>
								&copy; {new Date().getFullYear()} AgentFramer. All rights
								reserved.
							</span>
							<a
								href="https://verifieddr.com/website/agentframer-com"
								target="_blank"
								rel="noopener noreferrer"
							>
								<img
									src="https://verifieddr.com/badge/agentframer-com.svg"
									alt="Verified DR - Verified Domain Rating for agentframer.com"
									width={220}
									height={68}
								/>
							</a>
							<a
								href="https://agentmrr.com/agents/agentframer"
								target="_blank"
								rel="noreferrer"
							>
								<img
									src="https://agentmrr.com/api/listed-badge/agentframer?metric=mrr&style=default&theme=dark&currency=USD&v=2"
									alt="AgentFramer on AgentMRR"
									height={20}
								/>
							</a>
						</div>
						<div className="flex items-center gap-2">
							<span>Theme</span>
							<AnimatedThemeToggler size="sm" />
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}

function FooterColumn({
	title,
	links,
}: {
	title: string;
	links: Array<
		{ label: string; to: PublicRouteTo } | { label: string; href: string }
	>;
}) {
	return (
		<div className="space-y-3 text-sm">
			<p className="text-sm font-medium text-foreground">{title}</p>
			<div className="flex flex-col gap-2">
				{links.map((link) =>
					"href" in link ? (
						<a
							key={link.href + link.label}
							href={link.href}
							className="text-muted-foreground transition-colors hover:text-foreground"
						>
							{link.label}
						</a>
					) : (
						<Link
							key={link.to + link.label}
							to={link.to}
							className="text-muted-foreground transition-colors hover:text-foreground"
						>
							{link.label}
						</Link>
					),
				)}
			</div>
		</div>
	);
}

function FooterCta() {
	const [visible, setVisible] = useState(false);
	const [runId, setRunId] = useState(0);
	const ref = useCallback((node: HTMLDivElement | null) => {
		if (!node) return;
		let wasVisible = false;
		const replay = () => setRunId((id) => id + 1);
		const cleanups: Array<() => void> = [];

		if (typeof IntersectionObserver === "undefined") {
			setVisible(true);
			wasVisible = true;
			replay();
		} else {
			const observer = new IntersectionObserver(
				([entry]) => {
					const isVisible = Boolean(entry?.isIntersecting);
					setVisible(isVisible);
					if (isVisible && !wasVisible) replay();
					wasVisible = isVisible;
				},
				{ threshold: 0 },
			);
			observer.observe(node);
			cleanups.push(() => observer.disconnect());
		}

		const onRefocus = () => {
			if (document.visibilityState !== "visible") return;
			if (wasVisible) replay();
		};
		document.addEventListener("visibilitychange", onRefocus);
		window.addEventListener("focus", onRefocus);
		cleanups.push(() => {
			document.removeEventListener("visibilitychange", onRefocus);
			window.removeEventListener("focus", onRefocus);
		});

		return () => {
			for (const fn of cleanups) fn();
		};
	}, []);

	return (
		<div
			ref={ref}
			data-animate={visible ? "true" : undefined}
			className="relative overflow-hidden rounded-md bg-card px-6 py-10 text-foreground sm:px-10 sm:py-14"
		>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 text-foreground/10 opacity-60"
				style={{
					backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
					backgroundSize: "16px 16px",
				}}
			/>
			<div className="relative grid grid-cols-1 items-center gap-8 sm:grid-cols-[1.1fr_1fr]">
				<div className="max-w-lg">
					<h3 className="text-2xl font-semibold sm:text-3xl">
						Give your agent media tools.
					</h3>
					<p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
						One MCP setup. Images, videos, audio, job status, and stored output
						URLs.
					</p>
					<div className="mt-6 flex flex-wrap items-center gap-3">
						<Link
							to="/onboard"
							className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
						>
							Top up to connect
							<ArrowRight className="size-4" />
						</Link>
						<Link
							to="/help/quick-start"
							className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
						>
							Read quickstart
						</Link>
					</div>
				</div>
				<div className="hidden justify-end sm:flex">
					<LogoIcon
						key={runId}
						animateDraw
						aria-hidden="true"
						className="size-36 rounded-[22%] ring-1 ring-border"
					/>
				</div>
			</div>
		</div>
	);
}
